import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Badge } from "../../components/ui";
import {
  ArrowLeft,
  Package,
  CheckCircle2,
  Loader2,
  Shield,
  TrendingUp,
  AlertCircle,
  Wrench,
  Clock,
  AlertTriangle,
  Zap,
  BarChart3,
} from "lucide-react";
import { getAssetById, Asset } from "../../services/asset.api";
import { cn } from "../../lib/utils";
import { calculateHealthScore } from "../../utils/healthScore";
import { quoteTradeIn, quoteEnergySavings } from "../../services/businessRules.api";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getAssetAge(asset: Asset): number {
  const year =
    asset.purchaseYear ||
    (asset.purchasedAt ? new Date(asset.purchasedAt).getFullYear() : null);
  if (!year) return 0;
  return new Date().getFullYear() - year;
}

// Trade-in valuation and energy savings now come from the backend
// (`/api/v1/business-rules/trade-in` and `/energy-savings`). The previous
// straight-line depreciation and `0.16 * 0.25` energy formulas have been
// removed — they are computed server-side per tenant configuration.

function getConditionKey(score: number): string {
  if (score >= 8) return "conditionExcellent";
  if (score >= 6) return "conditionGood";
  if (score >= 4) return "conditionFair";
  if (score >= 2) return "conditionPoor";
  return "conditionNeedsReplacement";
}

function estimateAnnualRepairCost(asset: Asset): number {
  const records = asset.maintenanceRecords ?? [];
  if (records.length === 0) return 0;
  const totalCost = records.reduce((sum, r) => sum + (r.cost ?? 0), 0);
  const age = Math.max(1, getAssetAge(asset));
  return Math.round(totalCost / age);
}

// Annual energy cost is derived on the backend via
// `quoteEnergySavings`. Local kWh-rate constants are removed.

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UpgradeOffer() {
  const { t } = useTranslation('upgrade');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Server-quoted trade-in & energy values (replaces local formulas)
  const [tradeInValue, setTradeInValue] = useState(0);
  const [annualEnergyCost, setAnnualEnergyCost] = useState(0);
  const [annualEnergySavings, setAnnualEnergySavings] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAssetById(id)
      .then((data) => setAsset(data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('offer.loadError')))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch backend quotes once asset is loaded.
  useEffect(() => {
    if (!asset) return;
    let cancelled = false;

    // Trade-in: pass purchase price as baseValue + condition derived from health.
    const purchasePrice = asset.purchasePrice ?? 0;
    if (purchasePrice > 0) {
      const score = calculateHealthScore(asset);
      const condition: 'excellent' | 'good' | 'fair' | 'poor' =
        score >= 8 ? 'excellent' : score >= 6 ? 'good' : score >= 4 ? 'fair' : 'poor';
      const ageYears = getAssetAge(asset);
      quoteTradeIn({ baseValue: purchasePrice, condition, ageYears })
        .then((q) => { if (!cancelled) setTradeInValue(q.value); })
        .catch(() => { if (!cancelled) setTradeInValue(0); });
    } else {
      setTradeInValue(0);
    }

    // Energy savings: pass current annual kWh; server returns cost & savings.
    const kwh = asset.energyUsageKwh ?? 0;
    if (kwh > 0) {
      quoteEnergySavings({ currentKwhPerYear: kwh })
        .then((q) => {
          if (cancelled) return;
          setAnnualEnergyCost(q.currentAnnualCost ?? 0);
          setAnnualEnergySavings(q.annualSavings);
        })
        .catch(() => {
          if (cancelled) return;
          setAnnualEnergyCost(0);
          setAnnualEnergySavings(0);
        });
    } else {
      setAnnualEnergyCost(0);
      setAnnualEnergySavings(0);
    }

    return () => { cancelled = true; };
  }, [asset]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {error || t('offer.assetNotFound')}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate("/assets")}>
          {t('offer.backToAssets')}
        </Button>
      </div>
    );
  }

  // ── Calculations ───────────────────────────────────────────────────────────
  const healthScore = calculateHealthScore(asset);
  const conditionLabel = t(`offer.${getConditionKey(healthScore)}`);
  const assetAge = getAssetAge(asset);
  const annualRepairCost = estimateAnnualRepairCost(asset);
  const maintenanceCount =
    asset._count?.maintenanceRecords ?? asset.maintenanceRecords?.length ?? 0;

  const fiveYearRepairCost = annualRepairCost * 5;
  const fiveYearEnergyCost = annualEnergyCost * 5;
  const fiveYearKeepCost = fiveYearRepairCost + fiveYearEnergyCost;
  const fiveYearUpgradeBenefit =
    (annualEnergySavings + annualRepairCost) * 5 + tradeInValue;

  const hasActiveWarranty =
    asset.warrantyExpiresAt && new Date(asset.warrantyExpiresAt) > new Date();
  const hasCostData = annualRepairCost > 0 || annualEnergyCost > 0;

  // ── Recommendation ─────────────────────────────────────────────────────────
  const isUrgent = healthScore < 4 || assetAge > 14;
  const shouldConsider = !isUrgent && (healthScore < 6.5 || assetAge > 9);

  type Rec = {
    Icon: React.ElementType;
    label: string;
    title: string;
    body: string;
    color: string;
    bg: string;
    border: string;
  };

  const rec: Rec = isUrgent
    ? {
        Icon: AlertTriangle,
        label: t('offer.actionRecommended'),
        title: t('offer.urgentTitle'),
        body: t('offer.urgentBody', { age: assetAge > 0 ? t('offer.ageYears', { years: assetAge }) : t('offer.ageCurrent'), score: healthScore.toFixed(1) }),
        color: "text-amber-700 dark:text-amber-400",
        bg: "bg-amber-50 dark:bg-amber-950/30",
        border: "border-amber-200 dark:border-amber-800",
      }
    : shouldConsider
    ? {
        Icon: TrendingUp,
        label: t('offer.goodTimeReview'),
        title: t('offer.considerTitle'),
        body: t('offer.considerBody', { category: asset.category.toLowerCase() }),
        color: "text-blue-700 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-950/30",
        border: "border-blue-200 dark:border-blue-800",
      }
    : {
        Icon: CheckCircle2,
        label: t('offer.lookingGood'),
        title: t('offer.lookingGoodTitle'),
        body: t('offer.lookingGoodBody', { score: healthScore.toFixed(1), category: asset.category.toLowerCase() }),
        color: "text-green-700 dark:text-green-400",
        bg: "bg-green-50 dark:bg-green-950/30",
        border: "border-green-200 dark:border-green-800",
      };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur shadow-md">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(`/assets/${id}`)}
            className="h-8 w-8 rounded-full border flex items-center justify-center flex-shrink-0 hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm font-medium truncate">{t('offer.title')}</h1>
            <p className="text-xs text-muted-foreground truncate">{asset.name}</p>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10 space-y-8">

        {/* ── Recommendation Banner ──────────────────────────────────────── */}
        <div className={cn("rounded-2xl border p-6 flex gap-4", rec.bg, rec.border)}>
          <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 border", rec.border)}>
            <rec.Icon className={cn("h-5 w-5", rec.color)} />
          </div>
          <div className="min-w-0">
            <p className={cn("text-xs font-semibold uppercase tracking-widest mb-1", rec.color)}>
              {rec.label}
            </p>
            <h2 className="text-base font-semibold mb-2">{rec.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
              {rec.body}
            </p>
          </div>
        </div>

        {/* ── Asset Overview ─────────────────────────────────────────────── */}
        <div className="rounded-2xl border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-5">
            {t('offer.applianceSummary')}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-semibold">{asset.name}</h3>
              {(asset.brand || asset.model) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {[asset.brand, asset.model].filter(Boolean).join(" · ")}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-4">
                {assetAge > 0 && (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {t('offer.yearsOld', { count: assetAge })}
                  </span>
                )}
                {maintenanceCount > 0 && (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Wrench className="h-4 w-4" />
                    {t('offer.serviceRecord', { count: maintenanceCount })}
                  </span>
                )}
                {annualEnergyCost > 0 && (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Zap className="h-4 w-4" />
                    {t('offer.energyCostsPerYr', { amount: formatCurrency(annualEnergyCost) })}
                  </span>
                )}
                <Badge variant={hasActiveWarranty ? "default" : "secondary"} className="text-xs">
                  {hasActiveWarranty ? t('offer.manufacturerWarrantyActive') : t('offer.warrantyExpired')}
                </Badge>
              </div>
            </div>
            <div className="flex-shrink-0 rounded-xl border bg-muted/30 px-8 py-5 text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('offer.healthScore')}</p>
              <p className="text-4xl font-semibold tabular-nums leading-none">
                {healthScore.toFixed(1)}
                <span className="text-lg font-normal text-muted-foreground">/10</span>
              </p>
              <p className="text-sm text-muted-foreground mt-1.5">{conditionLabel}</p>
            </div>
          </div>
        </div>

        {/* ── 5-Year Cost Breakdown ──────────────────────────────────────── */}
        {hasCostData && (
          <div className="rounded-2xl border bg-card p-6">
            <div className="flex items-center gap-2.5 mb-6">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-base font-semibold">{t('offer.fiveYearBreakdown')}</h3>
              <span className="text-xs text-muted-foreground ml-1">
                {t('offer.fiveYearBreakdownHint')}
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {/* Keep */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                  {t('offer.ifKeep')}
                </p>
                <div className="space-y-3">
                  {annualRepairCost > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">
                        {t('offer.estimatedRepairs')}
                        <span className="block text-xs">{t('offer.estimatedRepairsDesc', { amount: formatCurrency(annualRepairCost) })}</span>
                      </span>
                      <span className="tabular-nums font-semibold ml-4 flex-shrink-0">
                        {formatCurrency(fiveYearRepairCost)}
                      </span>
                    </div>
                  )}
                  {annualEnergyCost > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">
                        {t('offer.energyCosts')}
                        <span className="block text-xs">{t('offer.energyCostsDesc', { kwh: asset.energyUsageKwh })}</span>
                      </span>
                      <span className="tabular-nums font-semibold ml-4 flex-shrink-0">
                        {formatCurrency(fiveYearEnergyCost)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm border-t pt-3 mt-1">
                    <span className="font-medium">{t('offer.fiveYearCost')}</span>
                    <span className="tabular-nums font-bold text-base">
                      {formatCurrency(fiveYearKeepCost)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Upgrade */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                  {t('offer.ifUpgrade')}
                </p>
                <div className="space-y-3">
                  {tradeInValue > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">
                        {t('offer.tradeInCredit')}
                        <span className="block text-xs">{t('offer.tradeInCreditDesc')}</span>
                      </span>
                      <span className="tabular-nums font-semibold text-green-600 ml-4 flex-shrink-0">
                        +{formatCurrency(tradeInValue)}
                      </span>
                    </div>
                  )}
                  {annualEnergySavings > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">
                        {t('offer.energySavings5yr')}
                        <span className="block text-xs">{t('offer.energySavings5yrDesc')}</span>
                      </span>
                      <span className="tabular-nums font-semibold text-green-600 ml-4 flex-shrink-0">
                        +{formatCurrency(annualEnergySavings * 5)}
                      </span>
                    </div>
                  )}
                  {annualRepairCost > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">
                        {t('offer.avoidedRepairs')}
                        <span className="block text-xs">{t('offer.avoidedRepairsDesc')}</span>
                      </span>
                      <span className="tabular-nums font-semibold text-green-600 ml-4 flex-shrink-0">
                        +{formatCurrency(fiveYearRepairCost)}
                      </span>
                    </div>
                  )}
                  {fiveYearUpgradeBenefit > 0 && (
                    <div className="flex justify-between items-center text-sm border-t pt-3 mt-1">
                      <span className="font-medium">{t('offer.totalBenefit')}</span>
                      <span className="tabular-nums font-bold text-base text-green-600">
                        +{formatCurrency(fiveYearUpgradeBenefit)}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                  {t('offer.pricingVaries')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Your Options ───────────────────────────────────────────────── */}
        <div>
          <h3 className="text-base font-semibold mb-1">{t('offer.yourOptions')}</h3>
          <p className="text-sm text-muted-foreground mb-5">
            {t('offer.yourOptionsDesc')}
          </p>

          <div className={cn("grid gap-5", !hasActiveWarranty ? "sm:grid-cols-2" : "sm:grid-cols-1 max-w-lg")}>
            {/* ── Protect It ── only show if warranty expired */}
            {!hasActiveWarranty && (
              <div className="rounded-2xl border bg-card p-6 flex flex-col">
                <div className="h-10 w-10 rounded-xl border flex items-center justify-center mb-5">
                  <Shield className="h-5 w-5" />
                </div>
                <h4 className="text-base font-semibold mb-2">{t('offer.protectIt')}</h4>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed flex-1">
                  {t('offer.protectItBody')}
                </p>
                <ul className="space-y-2.5 mb-6">
                  <li className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {t('offer.protectCovers')}
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {t('offer.protectTransparent')}
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {t('offer.protectMonthToMonth')}
                  </li>
                </ul>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    navigate(
                      `/warranty-plans?assetId=${asset.id}&assetName=${encodeURIComponent(
                        asset.name
                      )}&category=${encodeURIComponent(asset.category)}`
                    )
                  }
                >
                  {t('offer.viewProtectionPlans')}
                </Button>
              </div>
            )}

            {/* ── Upgrade It ── */}
            <div className="rounded-2xl border-2 border-foreground bg-card p-6 flex flex-col">
              <div className="flex items-start justify-between mb-5">
                <div className="h-10 w-10 rounded-xl border flex items-center justify-center">
                  <Package className="h-5 w-5" />
                </div>
                {tradeInValue > 0 && (
                  <Badge
                    variant="outline"
                    className="text-green-700 border-green-300 dark:text-green-400 dark:border-green-700 text-xs"
                  >
                    {t('offer.estimatedTradeIn', { amount: formatCurrency(tradeInValue) })}
                  </Badge>
                )}
              </div>
              <h4 className="text-base font-semibold mb-2">{t('offer.upgradeIt')}</h4>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed flex-1">
                {t('offer.upgradeBody', { category: asset.category.toLowerCase() })}
              </p>
              <ul className="space-y-2.5 mb-6">
                {tradeInValue > 0 && (
                  <li className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {t('offer.tradeInLine', { amount: formatCurrency(tradeInValue) })}
                  </li>
                )}
                <li className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {t('offer.fullWarranty')}
                </li>
                {annualEnergySavings > 0 && (
                  <li className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {t('offer.saveEnergy', { amount: formatCurrency(annualEnergySavings) })}
                  </li>
                )}
                <li className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {t('offer.freeDelivery')}
                </li>
              </ul>
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => navigate(`/assets/${id}/upgrade/browse`)}
              >
                {t('offer.browseNewModels')}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Disclaimer ─────────────────────────────────────────────────── */}
        <p className="text-xs text-muted-foreground text-center pb-4 max-w-2xl mx-auto leading-relaxed">
          {t('offer.disclaimer')}
        </p>
      </div>
    </div>
  );
}
